import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Breadcrumb, Card, Descriptions, Table, Tag, Spin, Empty,
  Button, Space, Typography,
} from 'antd';
import { ArrowLeftOutlined, HomeOutlined } from '@ant-design/icons';
import { getSourceContributions, SourceContribution } from '../api/products';
import { formatTimestamp } from '../utils/date';

const { Title, Text } = Typography;

export function SourceContributionPage() {
  const { pimProductId, sourceName } = useParams<{
    pimProductId: string;
    sourceName: string;
  }>();
  const navigate = useNavigate();

  const [data, setData] = useState<SourceContribution | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!pimProductId || !sourceName) return;
    setLoading(true);
    setError(null);
    getSourceContributions(Number(pimProductId), sourceName)
      .then(setData)
      .catch((err) => {
        setError(err?.response?.data?.message || 'Failed to load source contributions');
      })
      .finally(() => setLoading(false));
  }, [pimProductId, sourceName]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading source contributions...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <Empty description={error || 'Not found'}>
        <Button type="primary" onClick={() => navigate(-1)}>Go Back</Button>
      </Empty>
    );
  }

  const identifierColumns = [
    { title: 'Type', dataIndex: 'type', key: 'type',
      render: (v: string) => <Tag>{v.toUpperCase()}</Tag> },
    { title: 'Value', dataIndex: 'value', key: 'value',
      render: (v: string) => <Text copyable>{v}</Text> },
  ];

  const historyColumns = [
    { title: 'Time', dataIndex: 'timestamp', key: 'timestamp',
      render: (v: string) => formatTimestamp(v) },
    { title: 'Method', dataIndex: 'method', key: 'method',
      render: (v: string) => <Tag>{v}</Tag> },
    { title: 'Endpoint', dataIndex: 'endpoint', key: 'endpoint' },
    { title: 'Parameters', dataIndex: 'request_params', key: 'request_params',
      render: (v: Record<string, unknown> | null) =>
        v ? <Text code style={{ fontSize: 11 }}>{JSON.stringify(v)}</Text> : '-',
    },
    { title: 'Status', dataIndex: 'response_status', key: 'response_status',
      render: (v: number) => (
        <Tag color={v >= 200 && v < 300 ? 'green' : 'red'}>{v}</Tag>
      ),
    },
    { title: 'Time (ms)', dataIndex: 'response_time_ms', key: 'response_time_ms',
      render: (v: number | null) => v != null ? `${v}ms` : '-' },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Breadcrumb items={[
          { href: '/products', title: <><HomeOutlined /><span>Products</span></> },
          { href: `/products/pim_${pimProductId}`, title: data.product_summary.mfgpartno || `pim_${pimProductId}` },
          { title: `Source: ${sourceName}` },
        ]} />
      </div>

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/products/pim_${pimProductId}`)}>
              Back to Product
            </Button>
          </Space>
        </Card>

        <Card>
          <Title level={4} style={{ marginTop: 0 }}>
            <Tag color="orange" style={{ fontSize: 14 }}>{sourceName}</Tag>
            {' '}contributions to{' '}
            <Tag color="green" style={{ fontSize: 14 }}>pim_{pimProductId}</Tag>
          </Title>

          <Descriptions bordered size="small" column={2} style={{ marginBottom: 24 }}>
            <Descriptions.Item label="Product">{data.product_summary.description || '-'}</Descriptions.Item>
            <Descriptions.Item label="MPN">{data.product_summary.mfgpartno || '-'}</Descriptions.Item>
            <Descriptions.Item label="Manufacturer">{data.product_summary.manufacturer || '-'}</Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="Identifiers Contributed">
          {data.identifiers_contributed.length > 0 ? (
            <Table
              size="small"
              dataSource={data.identifiers_contributed.map((id, i) => ({ ...id, key: i }))}
              columns={identifierColumns}
              pagination={false}
            />
          ) : (
            <Empty description={`No identifier contributions recorded from ${sourceName}`} />
          )}
        </Card>

        <Card title="Request History">
          {data.request_history.length > 0 ? (
            <Table
              size="small"
              dataSource={data.request_history.map((r, i) => ({ ...r, key: i }))}
              columns={historyColumns}
              pagination={{ pageSize: 10 }}
            />
          ) : (
            <Empty description={`No API request history found for ${sourceName} involving this product`} />
          )}
        </Card>
      </Space>
    </div>
  );
}
